import { ref, type Ref } from 'vue';
import { downloadBlobFile, useCustomToast } from '@/shared/lib';
import { trainingContentApi, type TrainingContentListItem } from '@/entities/Training';

const PDF_MIME_TYPE = 'application/pdf';

export const TRAINING_CONTENT_VIEW_ERROR_MESSAGE = 'Erro ao acessar o conteúdo. Tente novamente';

function isPdf(content: TrainingContentListItem): boolean {
  if (content.file?.mimeType) {
    return content.file.mimeType === PDF_MIME_TYPE;
  }

  return content.file?.originalFileName?.toLowerCase().endsWith('.pdf') ?? false;
}

export function useTrainingContentViewer(): {
  openingContentIds: Ref<number[]>;
  openContent: (content: TrainingContentListItem) => Promise<void>;
} {
  const { showErrorToast } = useCustomToast();
  const openingContentIds = ref<number[]>([]);

  const showFailure = () => {
    showErrorToast(
      { title: TRAINING_CONTENT_VIEW_ERROR_MESSAGE, type: 'error' },
      { timeout: false, closeButton: true }
    );
  };

  const openContent = async (content: TrainingContentListItem) => {
    if (content.sourceType === 'URL') {
      if (!content.externalUrl) {
        showFailure();
        return;
      }

      window.open(content.externalUrl, '_blank', 'noopener');
      return;
    }

    if (openingContentIds.value.includes(content.id)) {
      return;
    }

    const shouldOpenInTab = isPdf(content);
    const pendingTab = shouldOpenInTab ? window.open('', '_blank', 'noopener') : null;

    openingContentIds.value = [...openingContentIds.value, content.id];

    try {
      const blob = await trainingContentApi.getTrainingContentFile(content.id);

      if (shouldOpenInTab) {
        const blobUrl = URL.createObjectURL(blob);

        if (pendingTab) {
          pendingTab.location.href = blobUrl;
        } else {
          window.open(blobUrl, '_blank', 'noopener');
        }

        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        return;
      }

      downloadBlobFile(blob, content.file?.originalFileName ?? `conteudo-${content.id}`);
    } catch {
      pendingTab?.close();
      showFailure();
    } finally {
      openingContentIds.value = openingContentIds.value.filter((id) => id !== content.id);
    }
  };

  return { openingContentIds, openContent };
}
